import { useEffect, useState } from "react";
import { GitBranch, Mail } from "lucide-react";
import { usePreIfrSettings, useSavePreIfrSettings } from "../../lib/api";
import { UNITS, splitDelay, toSeconds } from "../../lib/slaLadder";
import { NodeShell } from "./SlaNodes";
import { Select, Toggle, inputClass } from "./primitives";

const MAX_SECONDS = 7 * 24 * 3600;

const fromSettings = (data) => ({
  enabled: data?.first_message_enabled === true,
  template: data?.first_message_template || "",
  ...splitDelay(data?.first_message_delay_seconds ?? 1800),
});

// Why this cannot be saved, or "" when it can. Mirrors apps/api/pre_ifr_settings.py.
export function firstMessageProblem(form) {
  const seconds = toSeconds(form);
  if (form.amount === "" || !Number.isFinite(seconds) || seconds < 0 || seconds > MAX_SECONDS) {
    return "The wait must be between 0 (at once) and 7 days.";
  }
  if (form.enabled && !form.template.trim()) return "Enter the template to send before switching this on.";
  return "";
}

/**
 * The first message's settings, held here so the ladder's own Save can save them too. `save` writes them
 * (and throws if the server refuses); `reset` puts back what is saved. `ready` is false until they load.
 */
export function useFirstMessage(tenantId) {
  const { data } = usePreIfrSettings(tenantId);
  const mutation = useSavePreIfrSettings(tenantId);
  const [form, setForm] = useState(fromSettings(undefined));

  useEffect(() => {
    if (data) setForm(fromSettings(data));
  }, [data]);

  const saved = fromSettings(data);
  return {
    ready: !!data,
    form,
    set: (patch) => setForm((f) => ({ ...f, ...patch })),
    problem: firstMessageProblem(form),
    changed: !!data && (form.enabled !== saved.enabled || form.template.trim() !== saved.template || toSeconds(form) !== toSeconds(saved)),
    save: () =>
      mutation.mutateAsync({
        first_message_enabled: form.enabled,
        first_message_template: form.template.trim(),
        first_message_delay_seconds: toSeconds(form),
      }),
    reset: () => setForm(fromSettings(data)),
  };
}

// The template sent to the customer a set time after their job card arrives, drawn as the first node of
// the Pre-IFR journey. Its buttons tell us whether they are happy or unhappy.
export function FirstMessageNode({ fm }) {
  const { form, set } = fm;
  const template = form.template.trim();
  return (
    <NodeShell
      testId="pre-ifr-first-message"
      title="First message"
      badge={<Mail size={13} aria-hidden="true" />}
      icon={Mail}
      summary={template ? `Template: ${template}` : "No template yet"}
      missing={!template}
      off={!form.enabled}
      actions={<Toggle checked={form.enabled} onChange={(v) => set({ enabled: v })} label="First message active" />}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[13px] font-medium text-[#374151]">After</span>
        <input
          type="number"
          min="0"
          className={`${inputClass} !w-[76px] text-right`}
          value={form.amount}
          onChange={(e) => set({ amount: e.target.value })}
          aria-label="First message delay"
        />
        <Select className="w-[94px]" value={form.unit} onChange={(e) => set({ unit: e.target.value })} aria-label="First message delay unit">
          {UNITS.map((u) => (
            <option key={u.value} value={u.value}>
              {u.label}
            </option>
          ))}
        </Select>
        <span className="text-[13px] text-[#6B7280]">from the job card</span>
      </div>
      <input
        className={inputClass}
        value={form.template}
        placeholder="Template name"
        onChange={(e) => set({ template: e.target.value })}
        aria-label="First message template"
      />
    </NodeShell>
  );
}

// The customer's reply to the first message: happy ends it, unhappy logs the complaint and starts the steps below.
export function ReplyNode() {
  const cell = (dot, title, goes, testId) => (
    <div className="flex min-w-0 flex-col gap-2 px-3 py-3" data-testid={testId}>
      <div className="flex items-center gap-[6px] text-[13px] font-semibold text-[#101828]">
        <span className="h-[7px] w-[7px] flex-none rounded-full" style={{ background: dot }} aria-hidden="true" />
        {title}
      </div>
      <div className="text-[12px] text-[#667085]">{goes}</div>
    </div>
  );
  return (
    <div className="jp-node rounded-xl border border-[#D0D5DD] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.06)]" data-testid="pre-ifr-reply">
      <div className="flex items-center gap-2 border-b border-[#EAECF0] px-4 py-[10px]">
        <span className="flex h-5 w-5 flex-none rotate-45 items-center justify-center rounded-[5px] border border-[#98A2B3] text-[#475467]" aria-hidden="true">
          <GitBranch size={11} className="-rotate-45" />
        </span>
        <span className="text-[13px] font-semibold text-[#101828]">How does the customer reply?</span>
      </div>
      <div className="grid grid-cols-1 divide-y divide-[#EAECF0] sm:grid-cols-2 sm:divide-x sm:divide-y-0">
        {cell("#12B76A", "Happy", "Nothing more is sent", "pre-ifr-reply-happy")}
        {cell("#F04438", "Unhappy", "Log a complaint, start the steps below", "pre-ifr-reply-unhappy")}
      </div>
    </div>
  );
}
